import React, { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from '../services/supabaseClient'

// Create Auth Context
const AuthContext = createContext({})

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState(null)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) {
        console.error('Error getting session:', error)
      } else {
        setSession(session)
        setUser(session?.user ?? null)
      }
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session)
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const value = {
    user,
    session,
    loading,
    signUp: async (email, password, metadata = {}) => {
      setLoading(true)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      setLoading(false)
      return { data, error }
    },
    signIn: async (email, password) => {
      setLoading(true)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      setLoading(false)
      return { data, error }
    },
    signOut: async () => {
      setLoading(true)
      const { error } = await supabase.auth.signOut()
      setLoading(false)
      return { error }
    },
    resetPassword: async (email) => {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })
      return { data, error }
    },
    signInWithGoogle: async () => {
      try {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}`,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent'
            },
            skipBrowserRedirect: false
          }
        })
        
        if (error) {
          console.error('Google sign-in error:', error)
        }
        
        return { data, error }
      } catch (err) {
        console.error('Unexpected error during Google sign-in:', err)
        return { data: null, error: err }
      }
    }
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Custom hook for database operations
export const useSupabaseData = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execute = async (operation) => {
    setLoading(true)
    setError(null)
    try {
      const result = await operation()
      setLoading(false)
      return result
    } catch (err) {
      setError(err)
      setLoading(false)
      throw err
    }
  }

  const insert = async (table, data) => {
    return execute(async () => {
      const { data: result, error } = await supabase
        .from(table)
        .insert(data)
        .select()
      if (error) throw error
      return result
    })
  }

  const update = async (table, id, data) => {
    return execute(async () => {
      const { data: result, error } = await supabase
        .from(table)
        .update(data)
        .eq('id', id)
        .select()
      if (error) throw error
      return result
    })
  }

  const remove = async (table, id) => {
    return execute(async () => {
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id)
      if (error) throw error
      return true
    })
  }

  const fetch = async (table, filters = {}, options = {}) => {
    return execute(async () => {
      let query = supabase.from(table).select(options.select || '*')
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          query = query.in(key, value)
        } else {
          query = query.eq(key, value)
        }
      })
      
      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending ?? true })
      }
      
      // Apply limit
      if (options.limit) {
        query = query.limit(options.limit)
      }
      
      const { data, error } = await query
      if (error) throw error
      return data
    })
  }

  return {
    loading,
    error,
    insert,
    update,
    remove,
    fetch
  }
}

export default useAuth